/**
 * API client.
 *
 * The session cookie is HttpOnly and does the real work; the bearer token is
 * kept alongside it so the app still authenticates when a browser drops
 * third-party-ish cookies (Safari on iOS is the reason this exists).
 */

const BASE = import.meta.env.VITE_API_BASE ?? '/api';
const TOKEN_KEY = 'fc.token';

export class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private browsing - the cookie still carries the session */
  }
}

/** Fired when the server rejects our session, so the shell can bounce to login. */
export const onUnauthorized = new Set<() => void>();

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  let res: Response;

  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('No connection. Your work is saved on this device.', 0, null);
  }

  const text = await res.text();
  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }

  if (res.status === 401) {
    setToken(null);
    onUnauthorized.forEach((fn) => fn());
  }

  if (!res.ok) {
    throw new ApiError(data?.error ?? `Request failed (${res.status}).`, res.status, data);
  }

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/* ------------------------------------------------------------------ *
 * Shapes returned by the API
 * ------------------------------------------------------------------ */

export interface User {
  id: string;
  email: string;
  name: string;
  position: string | null;
  role: 'owner' | 'admin' | 'inspector';
  tenantId: string;
}

export interface Tenant {
  id: string;
  name: string;
  orgName: string | null;
  address: string | null;
  postcode: string | null;
  phone: string | null;
  email: string | null;
  vatNumber: string | null;
  defaultLocation: string | null;
  invoicePrefix: string;
  vatRate: number;
  labourRate: number;
}

export interface Customer {
  id: string;
  name: string;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  postcode: string | null;
  o_licence_no: string | null;
  o_licence_expiry: string | null;
  notes: string | null;
  asset_count?: number;
}

export interface Asset {
  id: string;
  customer_id: string | null;
  customer_name?: string | null;
  kind: 'vehicle' | 'trailer';
  reg_no: string | null;
  fleet_no: string | null;
  vin: string | null;
  chassis_no: string | null;
  make: string | null;
  model: string | null;
  body_type: string | null;
  axles: number;
  first_use_date: string | null;
  inspection_interval_weeks: number;
  last_inspection_date: string | null;
  next_inspection_date: string | null;
  mot_expiry: string | null;
  tacho_calibration_expiry: string | null;
  plating_expiry: string | null;
  tax_expiry: string | null;
  insurance_expiry: string | null;
  loler_expiry: string | null;
  odometer: number | null;
  odometer_unit: string;
  notes: string | null;
  status?: { state: string; days: number | null; due: string | null; label: string } | null;
  statusTitle?: string | null;
}

export interface Defect {
  id: string;
  inspection_id: string | null;
  asset_id: string;
  item_key: string | null;
  im_no: string | null;
  description: string;
  severity: 'safety' | 'repair' | 'monitor';
  action_taken: string | null;
  done_by: string | null;
  status: 'open' | 'rectified' | 'deferred';
  source: string;
  rectified_at: string | null;
  created_at: string;
  reg_no?: string | null;
  fleet_no?: string | null;
  kind?: string;
  customer_name?: string | null;
  inspection_ref?: string | null;
}

export interface Signature {
  id: string;
  kind: 'inspection' | 'rectification';
  name: string;
  position: string | null;
  image: string;
  signed_at: string;
}

export interface Inspection {
  id: string;
  ref: string;
  asset_id: string;
  form_code: 'VEH001' | 'VEH002';
  status: 'draft' | 'defects' | 'signed' | 'amended';
  iso_week: number | null;
  iso_year: number | null;
  date_in: string | null;
  inspection_date: string | null;
  location: string | null;
  inspection_organisation: string | null;
  operator_name: string | null;
  odometer: number | null;
  odometer_unit: string | null;
  header_extra: Record<string, any>;
  brake_test: Record<string, any>;
  brake_temp: Record<string, any>;
  tyres: Record<string, any>;
  brake_wear: Record<string, any>;
  comments: string | null;
  overall_result: string | null;
  tacho_download: string | null;
  inspector_name: string | null;
  inspector_position: string | null;
  signed_at: string | null;
  rectified_at: string | null;
  locked_at: string | null;
  content_hash: string | null;
  supersedes_id: string | null;
  hash_valid?: boolean;
  items: Record<string, { mc: string; note: string | null }>;
}

export interface InspectionSummary {
  id: string;
  ref: string;
  form_code: string;
  status: string;
  inspection_date: string | null;
  iso_week: number | null;
  iso_year: number | null;
  overall_result: string | null;
  inspector_name: string | null;
  signed_at: string | null;
  locked_at: string | null;
  reg_no: string | null;
  fleet_no: string | null;
  kind: string;
  make: string | null;
  model: string | null;
  customer_name: string | null;
  defect_count: number;
  open_defects: number;
}

export interface Job {
  id: string;
  ref: string;
  asset_id: string;
  customer_id: string | null;
  inspection_id: string | null;
  title: string;
  description: string | null;
  category: string | null;
  status: 'open' | 'in_progress' | 'complete' | 'invoiced';
  odometer: number | null;
  labour_hours: number;
  labour_rate: number;
  parts: { desc: string; qty: number; unitPrice: number }[];
  total: number;
  opened_at: string;
  completed_at: string | null;
  invoice_id: string | null;
  reg_no?: string | null;
  fleet_no?: string | null;
  kind?: string;
  customer_name?: string | null;
}

export interface Invoice {
  id: string;
  customer_id: string;
  customer_name?: string;
  number: string;
  issue_date: string;
  due_date: string | null;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'void';
  lines: { desc: string; qty: number; unitPrice: number; jobId?: string }[];
  subtotal: number;
  vat_rate: number;
  vat: number;
  total: number;
  paid_at: string | null;
  notes: string | null;
}

export interface DueItem {
  assetId: string;
  regNo: string | null;
  fleetNo: string | null;
  kind: string;
  make: string | null;
  model: string | null;
  customerName: string | null;
  title: string;
  key: string;
  state: 'overdue' | 'due' | 'soon' | 'ok' | 'unknown';
  days: number | null;
  due: string | null;
  label: string;
}

export interface Dashboard {
  counts: {
    assets: number;
    vehicles: number;
    trailers: number;
    overdue: number;
    dueSoon: number;
    openDefects: number;
    safetyDefects: number;
    inProgress: number;
  };
  due: DueItem[];
  openDefects: Defect[];
  inProgress: InspectionSummary[];
}
